import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdOutlineDeleteForever } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import {
  fetchDepartments,
  updateDepartment,
  deleteDepartment,
} from "../../redux/actions/departmentActions";

export default function DepartmentList() {
  const dispatch = useDispatch();
  const { data: departments, loading } = useSelector(
    (state) => state.department.department // Fixed to match the slice structure
  );

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [departmentToEdit, setDepartmentToEdit] = useState(null);

  useEffect(() => {
    dispatch(fetchDepartments());
  }, [dispatch]);

  const openDeleteModal = (department) => {
    setDepartmentToDelete(department);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDepartmentToDelete(null);
  };

  const confirmDelete = () => {
    dispatch(deleteDepartment(departmentToDelete.id));
    alert("Department deleted successfully!");
    closeDeleteModal();
  };

  // const openEditModal = (department) => {
  //   setDepartmentToEdit(department);
  //   setIsEditModalOpen(true);
  // };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setDepartmentToEdit(null);
  };

  const openEditModal = (department) => {
    if (department && department.id) {
      setDepartmentToEdit(department);
      setIsEditModalOpen(true);
    } else {
      console.error("Invalid department selected for editing:", department);
    }
  };
  
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!departmentToEdit || !departmentToEdit.id) {
      console.error("Invalid departmentToEdit state:", departmentToEdit);
      return;
    }
  
    const { id, department_name, description } = departmentToEdit;
    try {
      await dispatch(updateDepartment(id, { department_name, description }));
      alert("Department updated successfully!");
      closeEditModal(); // Close the modal after successful update
      
      // Re-fetch the departments to ensure the UI is up-to-date
      dispatch(fetchDepartments()); // Re-fetch the departments after an update
      
    } catch (error) {
      console.error("Error updating department:", error);
      alert("Failed to update department. Please try again.");
    }
  };
  
  

  if (loading) return <p>Loading departments...</p>;
  //if (error) return <p>Error: {error}</p>;

  return (
    <div className="p-4 bg-white border border-gray-300 rounded-lg shadow-md">
      <h2 className="mb-4 text-xl font-semibold text-gray-700">Department List</h2>
      <table className="w-full border border-gray-300 table-auto">
        <thead>
          <tr className="text-left text-gray-600 bg-gray-100">
            <th className="px-4 py-2">DEPARTMENT NAME</th>
            <th className="px-4 py-2">ACTIONS</th>
          </tr>
        </thead>
        <tbody>
       
          {departments.map((department) => (
            <tr key={department.id} className="border-b border-gray-300">
              <td className="px-4 py-2">{department.department_name}</td>
              <td className="px-4 py-2">
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    className="flex items-center px-5 py-1 text-gray-400 bg-gray-100 border border-gray-400 rounded"
                    onClick={() => openDeleteModal(department)}
                  >
                    Delete
                    <MdOutlineDeleteForever className="w-5 h-5 ml-2" />
                  </button>
                  <button
                    type="button"
                    className="flex items-center px-5 py-1 border rounded text-primary-color border-primary-color bg-primary-color bg-opacity-10"
                    onClick={() => openEditModal(department)}
                  >
                    Edit
                    <FaRegEdit className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
       

      </table>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50">
          <div className="w-[500px] p-8 bg-white rounded-lg shadow-lg text-center">
            <h3 className="mb-4 text-xl font-semibold text-gray-700">Delete?</h3>
            <p className="mb-6 text-gray-600">
              Are you sure you want to delete this department? <br />
              <strong>{departmentToDelete?.department_name}</strong>
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="px-6 py-2 text-gray-500 bg-gray-200 rounded-md hover:bg-gray-300"
                onClick={closeDeleteModal}
              >
                No
              </button>
              <button
                className="px-6 py-2 text-white bg-[#92288C] rounded-md"
                onClick={confirmDelete}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50">
          <div className="relative w-[800px] h-[400px] p-8 bg-white rounded-lg shadow-lg">
            <button
              type="button"
              className="absolute text-gray-500 border rounded-md shadow-md top-4 right-4 hover:text-gray-700"
              onClick={closeEditModal}
            >
              <IoClose className="w-6 h-6" />
            </button>
            <h3 className="mb-6 text-xl font-semibold text-gray-700">Edit Department</h3>
            <form onSubmit={handleUpdate}>
              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  Name
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  value={departmentToEdit?.department_name || ""}
                  onChange={(e) =>
                    setDepartmentToEdit({
                      ...departmentToEdit,
                      department_name: e.target.value,
                    })
                  }
                />
              </div>
              <div className="mb-6">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  Description
                </label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  rows="4"
                  value={departmentToEdit?.description || ""}
                  onChange={(e) =>
                    setDepartmentToEdit({
                      ...departmentToEdit,
                      description: e.target.value,
                    })
                  }
                  
                />
              </div>
              <div className="absolute flex space-x-4 bottom-6 right-4">
                <button
                  type="button"
                  className="px-6 py-2 text-gray-500 bg-gray-200 rounded-md hover:bg-gray-300"
                  onClick={closeEditModal}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
